import L from 'leaflet';
import type { Point, CategorieMarqueur } from '../../types/map';
import { ICON_SYMBOLS, ICON_SIZES, DEFAULT_MARKER_STYLE, DEFAULT_MARKER_IMAGE_SIZE } from '../../constants/map';
import { resolveAssetUrl } from '../../config/api';

// Import default icon
import iconDefault from 'leaflet/dist/images/marker-icon.png';

// Re-export constants for backward compatibility
export { ICON_SYMBOLS as iconSymbols, ICON_SIZES as markerSizes };

/** Facteur d'agrandissement appliqué au marqueur du projet sélectionné (§5.4). */
const SELECTED_SCALE = 1.35;

/**
 * Create a custom Leaflet icon based on category image, if available
 * @param isSelected - agrandit le marqueur (projet actuellement sélectionné/mis en avant)
 */
export const createCustomIcon = (
  styleIcone?: Point['styleIcone'],
  categorie?: CategorieMarqueur,
  isSelected?: boolean
): L.Icon | L.DivIcon => {
  // If category has a custom image marker, use L.icon (image-based)
  if (categorie?.typeIcone === 'image' && categorie.imageMarqueur) {
    const baseSize = categorie.tailleMarqueur || DEFAULT_MARKER_IMAGE_SIZE;
    const size = isSelected ? Math.round(baseSize * SELECTED_SCALE) : baseSize;
    return L.icon({
      iconUrl: resolveAssetUrl(categorie.imageMarqueur),
      iconSize: [size, size],
      iconAnchor: [size / 2, size],
      popupAnchor: [0, -size],
      className: `custom-image-marker${isSelected ? ' custom-image-marker--selected' : ''}`,
    });
  }

  // If category defines emoji appearance, use category values
  if (categorie) {
    const couleur = categorie.couleur || DEFAULT_MARKER_STYLE.couleur;
    const icone = categorie.icone || DEFAULT_MARKER_STYLE.icone;
    const taille = DEFAULT_MARKER_STYLE.taille;
    const baseSizeConfig = ICON_SIZES[taille as keyof typeof ICON_SIZES] || ICON_SIZES.medium;
    const iconSymbol = ICON_SYMBOLS[icone as keyof typeof ICON_SYMBOLS] || ICON_SYMBOLS.marker;
    const size = isSelected ? Math.round(baseSizeConfig.size * SELECTED_SCALE) : baseSizeConfig.size;
    const font = isSelected ? `calc(${baseSizeConfig.font} * ${SELECTED_SCALE})` : baseSizeConfig.font;

    const iconHtml = `
      <div class="custom-marker${isSelected ? ' custom-marker--selected' : ''}" style="
        background-color: ${couleur};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 2px solid #fff;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      ">
        <span style="
          transform: rotate(45deg);
          font-size: ${font};
          line-height: 1;
        ">${iconSymbol}</span>
      </div>
    `;

    return L.divIcon({
      html: iconHtml,
      iconSize: [size, size],
      iconAnchor: [size / 2, size],
      popupAnchor: [0, -size],
      className: 'custom-marker-icon'
    });
  }

  // Fallback: emoji DivIcon from point's own styleIcone
  if (!styleIcone) {
    return L.divIcon({
      html: `<img src="${iconDefault}" style="width: 25px; height: 41px;" />`,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      className: 'custom-div-icon'
    });
  }

  const {
    couleur = DEFAULT_MARKER_STYLE.couleur,
    icone = DEFAULT_MARKER_STYLE.icone,
    taille = DEFAULT_MARKER_STYLE.taille,
  } = styleIcone;
  const sizeConfig = ICON_SIZES[taille as keyof typeof ICON_SIZES] || ICON_SIZES.medium;
  const iconSymbol = ICON_SYMBOLS[icone as keyof typeof ICON_SYMBOLS] || ICON_SYMBOLS.marker;

  const iconHtml = `
    <div class="custom-marker" style="
      background-color: ${couleur};
      width: ${sizeConfig.size}px;
      height: ${sizeConfig.size}px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 2px solid #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    ">
      <span style="
        transform: rotate(45deg);
        font-size: ${sizeConfig.font};
        line-height: 1;
      ">${iconSymbol}</span>
    </div>
  `;

  return L.divIcon({
    html: iconHtml,
    iconSize: [sizeConfig.size, sizeConfig.size],
    iconAnchor: [sizeConfig.size / 2, sizeConfig.size],
    popupAnchor: [0, -sizeConfig.size],
    className: 'custom-marker-icon'
  });
};
